import React, {
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { Helmet } from 'react-helmet-async';
import { Button } from '@nextui-org/react';
import {
  AlertTriangle,
  CalendarDays,
  CalendarX2,
  Loader2,
  RotateCcw,
} from 'lucide-react';
import calendarService, {
  CalendarCategory,
  CalendarEvent,
} from '../services/calendarService';
import FeaturedEventsSlider from '../components/events/FeaturedEventsSlider';
import EventsFilterBar, { TimeMode } from '../components/events/EventsFilterBar';
import EventTimelineCard from '../components/events/EventTimelineCard';
import TimelineSkeleton from '../components/events/TimelineSkeleton';
import {
  ACCENT,
  dedupeEvents,
  endOfDayTs,
  eventHasCategory,
  eventKey,
  getEffectiveStatus,
  matchesSearch,
  MONTH_SHORT_DE,
  monthKeyOf,
  monthLabelOf,
  resolveCategory,
  startOfDayTs,
} from '../components/events/eventUtils';

const PAGE_SIZE = 120;
const PAST_RANGE_MS = 2 * 365 * 24 * 60 * 60 * 1000; // 2 Jahre zurück
const PAST_CHUNK = 40;

interface MonthGroup {
  key: string;
  label: string;
  events: CalendarEvent[];
}

const EventsTimelinePage: React.FC = () => {
  // ---------- Daten ----------
  const [upcomingEvents, setUpcomingEvents] = useState<CalendarEvent[]>([]);
  const [pastEvents, setPastEvents] = useState<CalendarEvent[]>([]);
  const [featured, setFeatured] = useState<CalendarEvent[]>([]);
  const [categories, setCategories] = useState<CalendarCategory[]>([]);

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [loadingMore, setLoadingMore] = useState(false);
  const [loadingPast, setLoadingPast] = useState(false);
  const [pastLoaded, setPastLoaded] = useState(false);
  const [upcomingOffset, setUpcomingOffset] = useState(0);
  const [hasMoreUpcoming, setHasMoreUpcoming] = useState(false);
  const [visiblePast, setVisiblePast] = useState(PAST_CHUNK);

  // ---------- Filter ----------
  const [mode, setMode] = useState<TimeMode>('upcoming');
  const [selectedCategoryId, setSelectedCategoryId] = useState<string | null>(null);
  const [selectedOrg, setSelectedOrg] = useState<string | null>(null);
  const [selectedMonthKey, setSelectedMonthKey] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [serverResults, setServerResults] = useState<CalendarEvent[]>([]);

  // ---------- Initial-Load ----------
  const loadInitial = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const now = Date.now();
      const [upcomingRes, ongoingRes, featuredRes, categoriesRes] = await Promise.allSettled([
        calendarService.getUpcomingEvents(PAGE_SIZE, 0),
        // Heute laufende (auch früher gestartete mehrtägige) Events dazuholen
        calendarService.getEventsByDateRange(startOfDayTs(now), endOfDayTs(now)),
        calendarService.getFeaturedEvents(10),
        calendarService.getAllCategories(),
      ]);

      if (upcomingRes.status === 'rejected' && ongoingRes.status === 'rejected') {
        throw new Error('Events konnten nicht geladen werden');
      }

      const upcoming = upcomingRes.status === 'fulfilled' ? upcomingRes.value : [];
      const ongoing = ongoingRes.status === 'fulfilled' ? ongoingRes.value : [];

      setUpcomingEvents(dedupeEvents([...ongoing, ...upcoming]));
      setUpcomingOffset(upcoming.length);
      setHasMoreUpcoming(upcoming.length >= PAGE_SIZE);
      setFeatured(featuredRes.status === 'fulfilled' ? featuredRes.value : []);
      setCategories(
        (categoriesRes.status === 'fulfilled' ? categoriesRes.value : []).filter(
          (c) => c.active !== false && (c.name || '').length > 0,
        ),
      );
    } catch (err) {
      console.error('Fehler beim Laden der Events:', err);
      setError('Events konnten nicht geladen werden.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadInitial();
  }, [loadInitial]);

  // ---------- Vergangene Events (lazy beim ersten Umschalten) ----------
  const loadPast = useCallback(async () => {
    setLoadingPast(true);
    try {
      const todayStart = startOfDayTs(Date.now());
      const events = await calendarService.getEventsByDateRange(
        todayStart - PAST_RANGE_MS,
        todayStart - 1,
      );
      const completed = events
        .filter((e) => e.startDate != null && getEffectiveStatus(e) !== 'ongoing')
        .sort((a, b) => (b.startDate ?? 0) - (a.startDate ?? 0));
      setPastEvents(completed);
      setPastLoaded(true);
    } catch (err) {
      console.error('Fehler beim Laden vergangener Events:', err);
      setError('Vergangene Events konnten nicht geladen werden.');
    } finally {
      setLoadingPast(false);
    }
  }, []);

  const handleModeChange = useCallback(
    (next: TimeMode) => {
      setMode(next);
      setVisiblePast(PAST_CHUNK);
      setSelectedMonthKey(null);
      if (next === 'past' && !pastLoaded && !loadingPast) {
        loadPast();
      }
    },
    [pastLoaded, loadingPast, loadPast],
  );

  // ---------- Mehr laden (kommende, server-seitig) ----------
  const loadMoreUpcoming = useCallback(async () => {
    setLoadingMore(true);
    try {
      const more = await calendarService.getUpcomingEvents(PAGE_SIZE, upcomingOffset);
      setUpcomingEvents((prev) => dedupeEvents([...prev, ...more]));
      setUpcomingOffset((prev) => prev + more.length);
      setHasMoreUpcoming(more.length >= PAGE_SIZE);
    } catch (err) {
      console.error('Fehler beim Nachladen der Events:', err);
    } finally {
      setLoadingMore(false);
    }
  }, [upcomingOffset]);

  // ---------- Server-Suche (debounced, ergänzt Client-Suche) ----------
  useEffect(() => {
    const term = searchTerm.trim();
    if (term.length < 2) {
      setServerResults([]);
      return undefined;
    }
    let cancelled = false;
    const timer = setTimeout(async () => {
      try {
        const results = await calendarService.searchEvents(term, 50);
        if (!cancelled) setServerResults(results);
      } catch {
        // Client-Suche greift weiterhin
      }
    }, 350);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [searchTerm]);

  // ---------- Kategorien-Map ----------
  const categoryById = useMemo(() => {
    const map = new Map<string, CalendarCategory>();
    categories.forEach((c) => {
      const id = c.id || c._id;
      if (id) map.set(id, c);
    });
    return map;
  }, [categories]);

  // ---------- Organisationen aus geladenen Events ----------
  const organizations = useMemo(() => {
    const set = new Set<string>();
    const base = mode === 'upcoming' ? upcomingEvents : pastEvents;
    base.forEach((e) => {
      if (e.organization) set.add(e.organization);
    });
    return Array.from(set).sort((a, b) => a.localeCompare(b, 'de'));
  }, [mode, upcomingEvents, pastEvents]);

  // ---------- Gefilterte + sortierte Events ----------
  const displayedEvents = useMemo(() => {
    const base = mode === 'upcoming' ? upcomingEvents : pastEvents;
    const term = searchTerm.trim();
    let list: CalendarEvent[];

    if (term.length > 0) {
      const clientMatches = base.filter((e) => matchesSearch(e, term));
      const now = Date.now();
      const todayStart = startOfDayTs(now);
      const serverInMode = serverResults.filter((e) => {
        if (e.startDate == null) return false;
        const endTs = endOfDayTs(e.endDate ?? e.startDate);
        return mode === 'upcoming' ? endTs >= now : e.startDate < todayStart && endTs < now;
      });
      list = dedupeEvents([...clientMatches, ...serverInMode]);
    } else {
      list = base;
    }

    if (selectedCategoryId) {
      list = list.filter((e) => eventHasCategory(e, selectedCategoryId));
    }
    if (selectedOrg) {
      list = list.filter((e) => e.organization === selectedOrg);
    }

    return [...list].sort((a, b) =>
      mode === 'upcoming'
        ? (a.startDate ?? 0) - (b.startDate ?? 0)
        : (b.startDate ?? 0) - (a.startDate ?? 0),
    );
  }, [mode, upcomingEvents, pastEvents, searchTerm, serverResults, selectedCategoryId, selectedOrg]);

  // ---------- Monats-Leiste: verfügbare Monate + Monats-Filter ----------
  const monthOptions = useMemo(() => {
    const seen = new Set<string>();
    const options: { key: string; label: string }[] = [];
    const years = new Set<number>();
    displayedEvents.forEach((e) => {
      if (e.startDate == null) return;
      years.add(new Date(e.startDate).getFullYear());
    });
    const showYear = years.size > 1;
    displayedEvents.forEach((e) => {
      if (e.startDate == null) return;
      const key = monthKeyOf(e.startDate);
      if (seen.has(key)) return;
      seen.add(key);
      const d = new Date(e.startDate);
      const label =
        MONTH_SHORT_DE[d.getMonth()].toUpperCase() +
        (showYear ? ` ’${String(d.getFullYear()).slice(2)}` : '');
      options.push({ key, label });
    });
    return options;
  }, [displayedEvents]);

  const visibleEvents = useMemo(() => {
    if (!selectedMonthKey) return displayedEvents;
    return displayedEvents.filter(
      (e) => e.startDate != null && monthKeyOf(e.startDate) === selectedMonthKey,
    );
  }, [displayedEvents, selectedMonthKey]);

  // Gewählten Monat verwerfen, wenn er (z.B. nach Filterwechsel) nicht mehr existiert
  useEffect(() => {
    if (selectedMonthKey && !monthOptions.some((m) => m.key === selectedMonthKey)) {
      setSelectedMonthKey(null);
    }
  }, [monthOptions, selectedMonthKey]);

  // ---------- Gruppierung: "Heute läuft" + Monatsgruppen ----------
  const { ongoingEvents, monthGroups, hasMorePast } = useMemo(() => {
    const now = Date.now();
    const limited = mode === 'past' ? visibleEvents.slice(0, visiblePast) : visibleEvents;

    const ongoing: CalendarEvent[] = [];
    const rest: CalendarEvent[] = [];
    limited.forEach((e) => {
      if (mode === 'upcoming' && getEffectiveStatus(e, now) === 'ongoing') ongoing.push(e);
      else rest.push(e);
    });

    const groups: MonthGroup[] = [];
    rest.forEach((e) => {
      if (e.startDate == null) return;
      const key = monthKeyOf(e.startDate);
      const last = groups[groups.length - 1];
      if (last && last.key === key) {
        last.events.push(e);
      } else {
        groups.push({ key, label: monthLabelOf(key), events: [e] });
      }
    });

    return {
      ongoingEvents: ongoing,
      monthGroups: groups,
      hasMorePast: mode === 'past' && visibleEvents.length > visiblePast,
    };
  }, [visibleEvents, mode, visiblePast]);

  const hasActiveFilters =
    selectedCategoryId !== null ||
    selectedOrg !== null ||
    selectedMonthKey !== null ||
    searchTerm.trim().length > 0;

  const resetFilters = () => {
    setSelectedCategoryId(null);
    setSelectedOrg(null);
    setSelectedMonthKey(null);
    setSearchTerm('');
  };

  const openEvent = (event: CalendarEvent) => {
    const url = event.sourceUrl || event.ticketUrl || event.registrationUrl;
    if (url) window.open(url, '_blank', 'noopener,noreferrer');
  };

  const timelineBusy = loading || (mode === 'past' && loadingPast);
  const upcomingCount = upcomingEvents.length;
  const isEmpty =
    !timelineBusy && !error && ongoingEvents.length === 0 && monthGroups.length === 0;

  return (
    <>
      <Helmet>
        <title>Event-Kalender | DartCloud</title>
        <meta
          name="description"
          content="Alle Dart-Events und Turniere von PDC, WDF, DDV & Co. als moderne Timeline: Termine, Venues, Preisgelder und Tickets auf einen Blick."
        />
        <link rel="canonical" href="https://dartcloudapp.de/kalender" />
      </Helmet>

      <div className="min-h-screen bg-zinc-950">
        {/* ============ Hero ============ */}
        <section className="relative overflow-hidden border-b border-zinc-800/80">
          <div className="absolute inset-0 bg-gradient-to-b from-zinc-900 via-zinc-950 to-zinc-950" />
          <div
            className="absolute -top-32 left-1/2 -translate-x-1/2 w-[640px] h-[320px] rounded-full blur-3xl opacity-20 pointer-events-none"
            style={{ backgroundColor: ACCENT }}
          />
          <div
            className="absolute -bottom-40 -right-24 w-[420px] h-[420px] rounded-full blur-3xl opacity-10 pointer-events-none"
            style={{ backgroundColor: ACCENT }}
          />

          <div className="relative max-w-5xl mx-auto px-4 md:px-6 py-12 md:py-16 text-center">
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-extrabold text-white tracking-tight mb-4">
              Event-
              <span
                className="text-transparent bg-clip-text"
                style={{ backgroundImage: `linear-gradient(90deg, ${ACCENT}, #7de8ea)` }}
              >
                Kalender
              </span>
            </h1>

            <p className="text-base md:text-lg text-gray-400 max-w-2xl mx-auto leading-relaxed">
              Alle professionellen Turniere und Events der großen Dart-Verbände –
              von der Weltmeisterschaft bis zur Pro Tour, als Timeline.
            </p>

            {!loading && upcomingCount > 0 && (
              <div className="mt-6 inline-flex items-center gap-2 px-4 py-2 rounded-full bg-zinc-900/80 border border-zinc-800 text-sm">
                <CalendarDays size={15} style={{ color: ACCENT }} />
                <span className="font-bold text-white">
                  {upcomingCount}
                  {hasMoreUpcoming ? '+' : ''}
                </span>
                <span className="text-gray-400">kommende Events</span>
              </div>
            )}
          </div>
        </section>

        {/* ============ Featured-Slider ============ */}
        {!loading && (
          <FeaturedEventsSlider
            events={featured}
            categoryById={categoryById}
            onOpenEvent={openEvent}
          />
        )}

        {/* ============ Filter-Leiste ============ */}
        <div className="mt-4">
          <EventsFilterBar
            mode={mode}
            onModeChange={handleModeChange}
            searchTerm={searchTerm}
            onSearchChange={setSearchTerm}
            categories={categories}
            selectedCategoryId={selectedCategoryId}
            onSelectCategory={setSelectedCategoryId}
            organizations={organizations}
            selectedOrg={selectedOrg}
            onSelectOrg={setSelectedOrg}
            months={monthOptions}
            selectedMonthKey={selectedMonthKey}
            onSelectMonth={setSelectedMonthKey}
          />
        </div>

        {/* ============ Timeline ============ */}
        <main className="max-w-5xl mx-auto px-3 sm:px-4 pb-16">
          {timelineBusy ? (
            <TimelineSkeleton />
          ) : error ? (
            <div className="text-center py-16">
              <AlertTriangle size={56} className="mx-auto text-gray-600 mb-4" />
              <p className="text-gray-400 mb-5">{error}</p>
              <Button
                onPress={() => {
                  setError(null);
                  if (mode === 'past') loadPast();
                  else loadInitial();
                }}
                style={{ backgroundColor: ACCENT, color: '#000' }}
                className="font-semibold"
              >
                Erneut versuchen
              </Button>
            </div>
          ) : isEmpty ? (
            <div className="text-center py-16">
              <CalendarX2 size={56} className="mx-auto text-gray-600 mb-4" />
              <h3 className="text-lg font-bold text-white mb-1">Keine Events gefunden</h3>
              <p className="text-gray-400 mb-5">
                {hasActiveFilters
                  ? 'Für die aktuelle Auswahl gibt es keine Events.'
                  : mode === 'past'
                    ? 'Es sind keine vergangenen Events vorhanden.'
                    : 'Aktuell sind keine kommenden Events eingetragen.'}
              </p>
              {hasActiveFilters && (
                <Button
                  onPress={resetFilters}
                  variant="bordered"
                  startContent={<RotateCcw size={15} />}
                  className="border-zinc-700 text-gray-300 font-semibold"
                >
                  Filter zurücksetzen
                </Button>
              )}
            </div>
          ) : (
            <div className="relative pl-8 sm:pl-12 pt-6">
              {/* Vertikale Timeline-Linie */}
              <div
                className="absolute left-3 sm:left-5 top-0 bottom-0 w-px sm:w-0.5 rounded-full"
                style={{
                  background: `linear-gradient(180deg, ${ACCENT}99 0%, ${ACCENT}33 60%, transparent 100%)`,
                }}
                aria-hidden="true"
              />

              {/* "Heute"-Gruppe: laufende Events */}
              {ongoingEvents.length > 0 && (
                <section aria-label="Heute laufende Events">
                  <div className="relative -ml-8 sm:-ml-12 pl-8 sm:pl-12 py-2">
                    <span
                      className="absolute left-[8px] sm:left-4 top-1/2 -translate-y-1/2 flex w-2.5 h-2.5"
                      aria-hidden="true"
                    >
                      <span
                        className="animate-ping absolute inline-flex h-full w-full rounded-full opacity-60"
                        style={{ backgroundColor: ACCENT }}
                      />
                      <span
                        className="relative inline-flex rounded-full w-2.5 h-2.5"
                        style={{ backgroundColor: ACCENT }}
                      />
                    </span>
                    <h2 className="text-lg font-bold" style={{ color: ACCENT }}>
                      Heute
                      <span className="ml-2 text-xs font-semibold text-gray-500">
                        {ongoingEvents.length}{' '}
                        {ongoingEvents.length === 1 ? 'Event läuft' : 'Events laufen'}
                      </span>
                    </h2>
                  </div>
                  <div className="space-y-4 pb-4">
                    {ongoingEvents.map((event) => (
                      <div key={eventKey(event)} className="relative">
                        <span
                          className="absolute -left-[26px] sm:-left-[33px] top-6 w-3 h-3 rounded-full border-2 border-zinc-950"
                          style={{ backgroundColor: ACCENT }}
                          aria-hidden="true"
                        />
                        <EventTimelineCard
                          event={event}
                          category={resolveCategory(event, categoryById)}
                        />
                      </div>
                    ))}
                  </div>
                </section>
              )}

              {/* Monatsgruppen */}
              {monthGroups.map((group) => (
                <section key={group.key} aria-label={group.label}>
                  <div className="relative -ml-8 sm:-ml-12 pl-8 sm:pl-12 py-2">
                    <span
                      className="absolute left-[8px] sm:left-4 top-1/2 -translate-y-1/2 w-2.5 h-2.5 rounded-full bg-zinc-700 border border-zinc-600"
                      aria-hidden="true"
                    />
                    <h2 className="text-lg font-bold text-white">
                      {group.label}
                      <span className="ml-2 text-xs font-semibold text-gray-500">
                        {group.events.length} {group.events.length === 1 ? 'Event' : 'Events'}
                      </span>
                    </h2>
                  </div>
                  <div className="space-y-4 pb-4">
                    {group.events.map((event) => (
                      <div key={eventKey(event)} className="relative">
                        <span
                          className="absolute -left-[26px] sm:-left-[33px] top-6 w-3 h-3 rounded-full bg-zinc-600 border-2 border-zinc-950"
                          aria-hidden="true"
                        />
                        <EventTimelineCard
                          event={event}
                          category={resolveCategory(event, categoryById)}
                        />
                      </div>
                    ))}
                  </div>
                </section>
              ))}

              {/* Mehr laden */}
              {((mode === 'upcoming' && hasMoreUpcoming) || hasMorePast) && (
                <div className="flex justify-center pt-2">
                  <Button
                    onPress={() => {
                      if (mode === 'upcoming') loadMoreUpcoming();
                      else setVisiblePast((prev) => prev + PAST_CHUNK);
                    }}
                    isDisabled={loadingMore}
                    variant="bordered"
                    className="border-zinc-700 text-gray-300 font-semibold hover:border-zinc-500"
                    startContent={
                      loadingMore ? (
                        <Loader2 size={15} className="animate-spin" />
                      ) : (
                        <CalendarDays size={15} />
                      )
                    }
                  >
                    {loadingMore ? 'Lädt…' : 'Mehr Events laden'}
                  </Button>
                </div>
              )}
            </div>
          )}
        </main>
      </div>
    </>
  );
};

export default EventsTimelinePage;
